import { clsx } from '@mentimeter/ragnar-tailwind-config';
import type { TextTransformProperty } from '@mentimeter/ragnar-styled';
import { QuestionMarkCircleIcon } from '@mentimeter/ragnar-visuals';
import * as React from 'react';
import type { ColorsEnum } from '@mentimeter/ragnar-colors';
import { Box, type BoxT } from '../box';
import type { PlacementT } from '../placement';
import { PopoverGeneric } from '../popover-legacy';
import { Text } from '../text';
import { TooltipGeneric, type TooltipGenericT } from './TooltipGeneric';

const DEFAULT_DELAY = 150;

interface GenericT extends BoxT {
  description: string;
  forceOneLine?: boolean;
}
const Generic = ({
  description,
  forceOneLine = true,
  width = 'auto',
  ...props
}: GenericT) => (
  <Box
    width={width}
    {...props}
    extend={() => ({
      whiteSpace: forceOneLine ? 'pre' : undefined,
    })}
    className={clsx(
      'rounded-xl',
      'bg-high-contrast',
      'py-2',
      'px-3',
      props?.className,
    )}
  >
    <Text className={clsx('text-on-high-contrast', 'text-87-5')}>
      {description}
    </Text>
  </Box>
);

export interface TooltipControlledT extends GenericT {
  referenceId: string;
  placement?: TooltipGenericT['placement'];
  show: boolean;
}

export const TooltipControlled = ({
  show,
  referenceId,
  placement,
  ...props
}: TooltipControlledT) => (
  <PopoverGeneric show={show} referenceId={referenceId} placement={placement}>
    <Generic {...props} />
  </PopoverGeneric>
);

export interface TooltipT extends BoxT {
  referenceId: string;
  description: string;
  delay?: number;
  forceOneLine?: boolean;
  placement?: TooltipGenericT['placement'];
  textTransform?: TextTransformProperty;
}

/**
 * @deprecated Use the new accessible tooltip components instead. [Link to Storybook](https://storybook.mentimeter.org/?path=/docs/global-system-ragnar-ui-tooltip--docs)
 */
export const TooltipOld = ({
  referenceId,
  description,
  delay = DEFAULT_DELAY,
  placement = 'top',
  forceOneLine = true,
  width = 'auto',
  textTransform = 'none',
  ...props
}: TooltipT) => (
  <TooltipGeneric
    referenceId={referenceId}
    bg="highContrast"
    delay={delay}
    placement={placement}
    ignorePointer
  >
    <Box
      width={width}
      {...props}
      extend={() => ({ whiteSpace: forceOneLine ? 'pre' : undefined })}
      className={clsx(
        'rounded-xl',
        'bg-high-contrast',
        'py-2',
        'px-3',
        props?.className,
      )}
    >
      <Text
        textTransform={textTransform}
        className={clsx('text-on-high-contrast', 'text-87-5')}
      >
        {description}
      </Text>
    </Box>
  </TooltipGeneric>
);

export interface TooltipDescriptiveT
  extends BoxT,
    Pick<TooltipGenericT, 'portalPosition'> {
  id: string;
  description: string;
  delay?: number;
  disabled?: boolean;
  forceOneLine?: boolean;
  placement?: PlacementT;
}

/**
 * @deprecated Use the new accessible tooltip components instead. [Link to Storybook](https://storybook.mentimeter.org/?path=/docs/global-system-ragnar-ui-tooltip--docs)
 */
export const TooltipDescriptive = ({
  id,
  description,
  delay = DEFAULT_DELAY,
  disabled,
  forceOneLine = true,
  placement = 'top',
  width = 'auto',
  maxWidth,
  portalPosition,
  color = 'primary',
  ...props
}: TooltipDescriptiveT) => (
  <>
    <TooltipGeneric
      referenceId={id}
      bg="highContrast"
      delay={delay}
      placement={placement}
      portalPosition={portalPosition}
    >
      <Generic
        description={description}
        forceOneLine={forceOneLine}
        width={width}
        maxWidth={maxWidth}
      />
    </TooltipGeneric>
    {disabled ? null : (
      <Box
        id={id}
        data-testid={`tooltip-${id}`}
        {...props}
        extend={() => ({ alignSelf: 'center' })}
        className={clsx(
          'ml-2',
          'inline-flex',
          'justify-center',
          'items-center',
          props?.className,
        )}
      >
        <QuestionMarkCircleIcon color={color as ColorsEnum} />
      </Box>
    )}
  </>
);
